/**
 * 3D Secure (3DS) Utilities
 * Handles 3DS detection and result extraction for payment flows
 *
 * 3DS is always required for credit card payments (visa, mastercard, amex).
 * Backend returns completed: false initially, then we poll /result/{lookup_key}
 * until 3DS challenge is resolved.
 */

import braintree from 'braintree-web';
import { paymentConfig } from '@config/Payment';

/**
 * Check if 3D Secure flow is required
 * 3DS is always required for credit card payments that haven't completed
 *
 * @param {string} method - Payment method (from paymentConfig.paymentMethods)
 * @param {Object} result - Payment result from /onetime or /recurring endpoint
 * @returns {boolean} True if 3DS flow should be initiated
 */
function requires3ds(method, result) {
  const is3dsRequiredForMethod =
    paymentConfig.threeDS.alwaysRequiredForCreditCard &&
    method === paymentConfig.paymentMethods.CREDIT_CARD;

  const isIncomplete = result?.completed === false;
  const hasLookupKey = !!result?.lookup_key;

  // console.log('[3DS] Checking if 3DS required:', {
  //   method,
  //   is3dsRequiredForMethod,
  //   isIncomplete,
  //   hasLookupKey,
  //   result: result ? '...' : null
  // });

  return is3dsRequiredForMethod && isIncomplete && hasLookupKey;
}

/**
 * Extract 3DS lookup key from payment result
 * Used to poll /result/{lookup_key} for completion status
 *
 * @param {Object} result - Payment result
 * @returns {string|null} Lookup key or null if not found
 */
function extract3dsLookupKey(result) {
  const lookupKey = result?.lookup_key || null;

  // if (lookupKey) {
  //   console.log('[3DS] Extracted lookup key:', lookupKey);
  // }

  return lookupKey;
}

/**
 * Verify card with 3D Secure using Braintree SDK
 * Braintree handles 3DS challenge natively (modal appears if needed)
 *
 * @param {Object} client - Braintree client instance
 * @param {string} tokenizedNonce - Card nonce from hosted fields
 * @param {number} amount - Payment amount
 * @param {string} msisdn - Mobile phone number for additional info
 * @param {string} bin - Bank Identification Number (first 6 digits of card)
 * @returns {Promise<string>} Verified nonce for payment submission
 * @throws {Error} If verification fails
 */
async function verify3DSCard(client, tokenizedNonce, amount, msisdn, bin) {
  let threeDSInstance = null;

  try {
    threeDSInstance = await braintree.threeDSecure.create({
      client,
      version: 2
    });

    // console.log('[3DS] Created 3DS instance');

    // Format amount as per Braintree requirements
    // Amount may come as string or number from frontend, convert to string with 2 decimals
    // Handle 0 (number) correctly - it's falsy but valid, so check for null/undefined instead
    const formattedAmount = amount != null ? parseFloat(amount).toFixed(2) : '0.00';

    // NOTE: WORKAROUND: Braintree 3DS SDK doesn't accept amount=0.00 for verification
    // Use minimal amount (0.01) for 3DS verification when storing payment method (amount=0)
    // Backend still receives amount=0.00 as required - this is client-side only
    // const verificationAmount = formattedAmount === '0.00' ? '0.01' : formattedAmount;
    // console.log(
    //   '[3DS] Amount formatting - original:',
    //   formattedAmount,
    //   'for verification:',
    //   verificationAmount
    // );

    // Build verification options - BIN is required for Hosted Fields
    const verifyOptions = {
      nonce: tokenizedNonce,
      amount: formattedAmount, // use actual amount, no workaround
      // amount: verificationAmount, // NOTE: Use WORKAROUND amount for verification
      additionalInformation: { mobilePhoneNumber: msisdn },
      onLookupComplete: (data, next) => {
        // console.log('[3DS] Lookup complete, proceeding with challenge');
        next();
      }
    };

    // Add BIN if provided (required for Web Hosted Fields)
    if (bin && bin.length === 6) {
      verifyOptions.bin = bin;
      // console.log('[3DS] Using BIN for verification:', bin);
    } else {
      // console.log(
      //   '[3DS] BIN not available (length:',
      //   bin?.length || 'undefined',
      //   '), attempting 3DS without BIN'
      // );
    }

    // verifyCard handles 3D Secure challenge natively using Promise API
    // Braintree 3DS 2.0 shows modal if challenge is required
    // console.log('[3DS] Calling verifyCard...');

    let payload;
    try {
      payload = await threeDSInstance.verifyCard(verifyOptions);
    } catch (err) {
      // console.error('[3DS] ❌ Verification error:', {
      //   code: err.code,
      //   message: err.message
      // });

      // Check if this is a user cancellation based on EXPLICIT error messages only
      // Note: THREEDS_LOOKUP_VALIDATION_ERROR is used for card validation failures, not cancellations
      // Main cancellation detection happens via success payload (liabilityShiftPossible && !liabilityShifted)
      const errorMessage = err?.message?.toLowerCase() || '';
      const isCancellation =
        errorMessage.includes('customer canceled') ||
        errorMessage.includes('user closed') ||
        errorMessage.includes('authentication canceled') ||
        errorMessage.includes('user cancelled') ||
        errorMessage.includes('cancelled by user');

      if (isCancellation) {
        // console.log('[3DS] ✅ Detected as user cancellation from error message');
        const error = new Error('3D Secure verification cancelled by user');
        error.cancelled = true;
        throw error;
      }

      // Not a cancellation, rethrow original error (will show error to user)
      // console.log('[3DS] ❌ Real verification error - will show error component to user');
      throw err;
    }

    // console.log('[3DS] ✅ Verification complete');
    // console.log('[3DS] Payload details:', {
    //   hasNonce: !!payload?.nonce,
    //   liabilityShifted: payload?.liabilityShifted,
    //   liabilityShiftPossible: payload?.liabilityShiftPossible,
    //   threeDSecureInfo: payload?.threeDSecureInfo
    // });

    // CRITICAL: Check if user cancelled the 3DS challenge
    // Braintree returns SUCCESS even when user closes the modal, but with:
    // - liabilityShifted: false (3DS not completed)
    // - liabilityShiftPossible: true (3DS was available)
    // This indicates user was shown the challenge but didn't complete it
    if (payload?.liabilityShiftPossible && !payload?.liabilityShifted) {
      // console.log('[3DS] ⚠️ User did not complete 3DS challenge - treating as cancellation');
      // console.log('[3DS] Partial verification data:', {
      //   hasNonce: !!payload.nonce,
      //   liabilityShifted: payload.liabilityShifted,
      //   liabilityShiftPossible: payload.liabilityShiftPossible
      // });
      const error = new Error('3D Secure verification cancelled by user');
      error.cancelled = true;
      throw error;
    }

    // Extract verified nonce from response
    const verifiedNonce = payload?.nonce || payload?.paymentMethod?.nonce || tokenizedNonce;

    // console.log('[3DS] Extracted verified nonce');
    return verifiedNonce;
  } catch (err) {
    // console.error('[3DS] Card verification failed:', err?.message || 'Unknown error');

    // If this was a user cancellation, preserve the cancelled flag
    if (err?.cancelled) {
      // console.log('[3DS] Rethrowing cancellation error');
      throw err; // Rethrow as-is to preserve cancelled flag
    }

    // For other errors, provide a user-friendly error message
    const userMessage =
      err?.code === 'THREEDS_CARDINAL_SDK_ERROR'
        ? '3D Secure verification failed due to an authentication provider error. Please try again or use a different payment method.'
        : '3D Secure verification failed. Please try again.';
    const wrapped = new Error(userMessage);
    // keep original error available for callers / logging
    wrapped.original = err;
    throw wrapped;
  } finally {
    // Cleanup 3DS instance
    try {
      if (threeDSInstance && typeof threeDSInstance.teardown === 'function') {
        await threeDSInstance.teardown();
        // console.log('[3DS] Instance torn down');
      }
    } catch (e) {
      // console.warn('[3DS] Teardown error:', e?.message || 'Unknown error');
      console.warn('[3DS] Teardown error');
    }
  }
}

export default {
  requires3ds,
  extract3dsLookupKey,
  verify3DSCard
};
